export const PE_ROUTE = '/program-evaluation';

export type ListInfo = { title: string; verb: string; from: string; fromTo: string };

export type ListContext = {
  cohortLabels?: Record<string, string>;
  gradeLabels?: Record<string | number, string>;
};

const HEADLINE: Record<string, [string, string]> = {
  active: ['Active EL students', 'in the program now'],
  exited: ['Exited this year', 'left the program this year'],
  monitored: ['Monitored EL students', 'in monitoring'],
  met: ['Met exit criteria', 'met the exit criteria'],
  review: ['Eligible for review', 'met the review bar, not the exit bar'],
  newcomer: ['Newcomer ELs', 'are newcomers'],
  ltel: ['Long-term ELs', 'are long-term ELs'],
  dual: ['Dually identified', 'are dually identified'],
  newid: ['Newly identified', 'were identified this year'],
  served: ['Served this year', 'were served this year'],
  atrisk: ['At risk of becoming long-term', 'are a year or two from long-term'],
};

const NOTES: Record<string, [string, string]> = {
  unreadable_status: ["EL status we couldn't read", "have an EL status we couldn't read"],
  no_signal: ['Roster rows with no EL signal', 'carry no EL signal'],
  exit_undated: ['Exits with no date', 'exited with no date on file'],
  newcomer_confirm: ['Newcomer standing needs confirmation', 'need their newcomer standing confirmed'],
  ltel_confirm: ['Long-term standing needs confirmation', 'need their long-term standing confirmed'],
  exit_conflict: ['Exit dates that conflict with later testing', 'have an exit date that conflicts with later testing'],
  monitoring_disagrees: [
    'Monitoring status disagrees with the exit date',
    'have a monitoring status that disagrees with the exit date',
  ],
};

const START_LABELS: Record<string, string> = {
  1: 'started at level 1',
  2: 'started at level 2',
  3: 'started at level 3',
  4: 'started at level 4 or above',
  none: 'had no score last year',
};

const COHORT_LABELS: Record<string, string> = {
  ltel: 'Long-term ELs',
  newcomer: 'Newcomers',
  review: 'Eligible for review',
  dual: 'Dually identified',
};

const encode = (segment: string): string => encodeURIComponent(segment);

const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
];
const SHA256_H = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

function sha256Hex(bytes: Uint8Array): string {
  const padded = new Uint8Array(((bytes.length + 9 + 63) >> 6) << 6);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor((bytes.length * 8) / 0x100000000));
  view.setUint32(padded.length - 4, (bytes.length * 8) >>> 0);
  const w = new DataView(new ArrayBuffer(256));
  const state = new DataView(new ArrayBuffer(32));
  SHA256_H.forEach((h, i) => state.setUint32(i * 4, h));
  for (let offset = 0; offset < padded.length; offset += 64) {
    for (let i = 0; i < 16; i += 1) w.setUint32(i * 4, view.getUint32(offset + i * 4));
    for (let i = 16; i < 64; i += 1) {
      const w15 = w.getUint32((i - 15) * 4);
      const w2 = w.getUint32((i - 2) * 4);
      const s0 = rotr(w15, 7) ^ rotr(w15, 18) ^ (w15 >>> 3);
      const s1 = rotr(w2, 17) ^ rotr(w2, 19) ^ (w2 >>> 10);
      w.setUint32(i * 4, (w.getUint32((i - 16) * 4) + s0 + w.getUint32((i - 7) * 4) + s1) >>> 0);
    }
    const round = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => state.getUint32(i * 4));
    let [a = 0, b = 0, c = 0, d = 0, e = 0, f = 0, g = 0, h = 0] = round;
    for (const [i, k] of SHA256_K.entries()) {
      const t1 = (h + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + k + w.getUint32(i * 4)) >>> 0;
      const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    [a, b, c, d, e, f, g, h].forEach((v, i) => state.setUint32(i * 4, (state.getUint32(i * 4) + v) >>> 0));
  }
  return [0, 1, 2, 3, 4, 5, 6, 7].map((i) => state.getUint32(i * 4).toString(16).padStart(8, '0')).join('');
}

export const MAX_LIST_KEY_BYTES = 120;
const LIST_REF_PREFIX = 'ref:';
const LIST_REF = /^ref:[0-9a-f]{32}$/;

export const listRefOf = (key: string): string => sha256Hex(new TextEncoder().encode(key)).slice(0, 32);
export const isListRef = (selector: string): boolean => LIST_REF.test(selector);

export function listSelector(key: string): string {
  if (isListRef(key) || new TextEncoder().encode(key).length <= MAX_LIST_KEY_BYTES) return key;
  return `${LIST_REF_PREFIX}${listRefOf(key)}`;
}

export function loggableListKey(key: string): string {
  if (new TextEncoder().encode(key).length <= MAX_LIST_KEY_BYTES) return key;
  const parts = key.split(':');
  const school = parts.indexOf('school');
  if (school >= 0 && school + 1 < parts.length) parts[school + 1] = '*';
  return parts.join(':');
}

export function listQuery(selector: string): { key: string } | { ref: string } {
  const resolved = listSelector(selector);
  return isListRef(resolved) ? { ref: resolved.slice(LIST_REF_PREFIX.length) } : { key: resolved };
}

export const schoolKeyComponent = (school: string): string => school.replace(/%/g, '%25').replace(/:/g, '%3A');
const decodeSchool = (component: string): string => component.replace(/%3A/gi, ':').replace(/%25/g, '%') || 'No school on file';

export const gradeHref = (grade: number | string): string => `${PE_ROUTE}/grade/${encode(String(grade))}`;
export const BLANK_SCHOOL_PARAM = ':';
const schoolParamOf = (component: string): string =>
  component === '' || component === '.' || component === '..' ? `${BLANK_SCHOOL_PARAM}${component}` : component;
export const schoolHref = (component: string): string => `${PE_ROUTE}/school/${encode(schoolParamOf(component))}`;
export function schoolComponentOf(key: string): string {
  const parts = key.split(':');
  const at = parts.indexOf('school');
  return at === -1 ? '' : (parts[at + 1] ?? '');
}
export const schoolParamToComponent = (param: string): string => (param.startsWith(BLANK_SCHOOL_PARAM) ? param.slice(BLANK_SCHOOL_PARAM.length) : param);
export const cohortHref = (cohort: string): string => `${PE_ROUTE}/cohort/${encode(cohort)}`;
export const studentsHref = (key: string): string => `${PE_ROUTE}/students/${encode(listSelector(key))}`;
export const studentHref = (ref: string, key: string): string =>
  `${PE_ROUTE}/student/${encode(ref)}/${encode(listSelector(key))}`;

export const GRADE_NOT_ON_FILE = 'Not on file';
export const GRADE_PRE_K = -3;
export const GRADE_UNKNOWN = -1;

export function gradeShortLabel(grade: number, label?: string): string {
  if (grade === GRADE_PRE_K) return 'PK';
  if (grade < 0) return 'None';
  return grade === 0 ? 'K' : label || String(grade);
}

export function gradeName(grade: number | string, label?: string): string {
  const numeric = typeof grade === 'number' ? grade : Number.parseInt(grade, 10);
  if (numeric === GRADE_PRE_K || /^(pk|pre-k|prek)$/i.test(label ?? '')) return 'Pre-K';
  if (numeric < 0 || label === GRADE_NOT_ON_FILE) return 'Grade not on file';
  if (numeric === 0 || /^(k|kg|kindergarten)$/i.test(label ?? '')) return 'Kindergarten';
  const shown = Number.isNaN(numeric) ? (label ?? String(grade)) : String(numeric);
  return `Grade ${shown}`;
}

export function describeListKey(key: string, ctx: ListContext = {}): ListInfo {
  if (isListRef(key)) return { title: 'Students', verb: 'are in this list', from: 'Program Evaluation', fromTo: PE_ROUTE };
  const parts = key.split(':');
  const [family, second, third, fourth] = parts;
  const grade = (g: string) => gradeName(g, ctx.gradeLabels?.[g]);
  const lower = (g: string) => grade(g).toLowerCase();
  if (family === 'note' && second && NOTES[second]) {
    const [title, verb] = NOTES[second] as [string, string];
    return { title, verb, from: 'About these counts', fromTo: PE_ROUTE };
  }
  if (family === 'grade' && second !== undefined) {
    if (third === 'school' && fourth !== undefined) {
      const school = decodeSchool(fourth);
      const met = parts[4] === 'met';
      return {
        title: `${grade(second)} · ${school}${met ? ' · met exit criteria' : ''}`,
        verb: `in ${lower(second)} at ${school}${met ? ' met the exit criteria' : ''}`,
        from: `Grade detail · ${grade(second)}`,
        fromTo: gradeHref(second),
      };
    }
    if (third === 'others') {
      return {
        title: `${grade(second)} · all other ELs`,
        verb: `in ${lower(second)} are neither newcomers nor long-term ELs`,
        from: `Grade detail · ${grade(second)}`,
        fromTo: gradeHref(second),
      };
    }
    if (third === 'met') {
      return {
        title: `${grade(second)} · met exit criteria`,
        verb: `in ${lower(second)} met the exit criteria`,
        from: 'Section 2.2 · Composite growth by grade',
        fromTo: PE_ROUTE,
      };
    }
    if (third === 'start' && fourth !== undefined) {
      const start = START_LABELS[fourth] ?? `started at ${fourth}`;
      return {
        title: `${grade(second)} · ${start}`,
        verb: `in ${lower(second)} ${start}`,
        from: `Grade detail · ${grade(second)}`,
        fromTo: gradeHref(second),
      };
    }
    return {
      title: grade(second),
      verb: `are in ${lower(second)}`,
      from: 'Section 2.2 · Composite growth by grade',
      fromTo: PE_ROUTE,
    };
  }
  if (family === 'school' && second !== undefined) {
    const school = decodeSchool(second);
    if (third === 'met') {
      return {
        title: `${school} · met exit criteria`,
        verb: `at ${school} met the exit criteria`,
        from: `School detail · ${school}`,
        fromTo: schoolHref(second),
      };
    }
    const gradePart = third === 'grade' && fourth !== undefined ? grade(fourth) : null;
    const start = parts[4] === 'start' && parts[5] !== undefined ? (START_LABELS[parts[5]] ?? `started at ${parts[5]}`) : null;
    return {
      title: `${school}${gradePart ? ` · ${gradePart}` : ''}${start ? ` · ${start}` : ''}`,
      verb: `at ${school}${gradePart ? `, ${gradePart.toLowerCase()}` : ''}${start ? `, ${start}` : ''}`,
      from: `School detail · ${school}`,
      fromTo: schoolHref(second),
    };
  }
  if (family === 'cohort' && second !== undefined) {
    const label = ctx.cohortLabels?.[second] ?? COHORT_LABELS[second] ?? second;
    const base = HEADLINE[second]?.[1] ?? 'are in this cohort';
    if (third === 'grade' && fourth !== undefined) {
      const met = parts[4] === 'met';
      return {
        title: `${label} · ${grade(fourth)}${met ? ' · met exit criteria' : ''}`,
        verb: `${base}, ${lower(fourth)}${met ? ', who met the exit criteria' : ''}`,
        from: `Cohort detail · ${label}`,
        fromTo: cohortHref(second),
      };
    }
    if (third === 'school' && fourth !== undefined) {
      const school = decodeSchool(fourth);
      const met = parts[4] === 'met';
      return {
        title: `${label} · ${school}${met ? ' · met exit criteria' : ''}`,
        verb: `${base}, ${school}${met ? ', who met the exit criteria' : ''}`,
        from: `Cohort detail · ${label}`,
        fromTo: cohortHref(second),
      };
    }
    if (third === 'met') {
      return { title: `${label} · met exit criteria`, verb: `${base} who met the exit criteria`, from: `Cohort detail · ${label}`, fromTo: cohortHref(second) };
    }
    return { title: label, verb: base, from: 'Section 4 · Cohort baselines', fromTo: PE_ROUTE };
  }
  const head = HEADLINE[key];
  if (head) {
    const title = COHORT_LABELS[key] && ctx.cohortLabels?.[key] ? ctx.cohortLabels[key] : head[0];
    return { title: title ?? head[0], verb: head[1], from: 'Headline numbers', fromTo: PE_ROUTE };
  }
  return { title: key, verb: 'are in this count', from: 'Headline numbers', fromTo: PE_ROUTE };
}
